import type { Status } from '@krit/core';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from './Menu.js';
import { ACTIVE_STAGES, CLOSED_STAGES, CLOSED_NOTE, STAGE_DOT, stageLabel, isClosedStage } from '../lib/stages.js';

/**
 * The deal's pipeline stage, as a coloured pill that opens a stage picker.
 *
 * Replaces a bare <select>. Two reasons it's worth the component: the stage colour now matches the
 * CRM board, so a deal reads the same in both places; and the picker separates the live pipeline
 * from the two terminal stages under a CLOSED heading, so "Not Interested" — which effectively
 * retires a deal — can't be hit by a mis-aimed click on a flat nine-item list.
 */
export function StagePill({
  status,
  onChange,
  disabled = false,
}: {
  status: string;
  onChange: (s: Status) => void;
  disabled?: boolean;
}) {
  const dot = STAGE_DOT[status] ?? 'bg-muted-2';

  return (
    <Menu
      label={`Change stage — currently ${stageLabel(status)}`}
      trigger={(open) => (
        <span
          className={`inline-flex items-center gap-2 bg-white/10 border text-white text-[12px] rounded-lg px-2.5 py-1.5
            ${open ? 'border-white/40 bg-white/20' : 'border-white/20 hover:bg-white/20'}
            ${disabled ? 'opacity-50' : ''}`}
        >
          <span className={`w-2 h-2 rounded-full flex-none ${dot}`} />
          <span className="font-semibold">{stageLabel(status)}</span>
          <span className={`text-[9px] opacity-70 transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
        </span>
      )}
    >
      {(close) => (
        <>
          <MenuLabel>Pipeline</MenuLabel>
          {ACTIVE_STAGES.map((s) => (
            <StageRow key={s} stage={s} current={status} onPick={onChange} close={close} />
          ))}
          <MenuSeparator />
          <MenuLabel>Closed</MenuLabel>
          {CLOSED_STAGES.map((s) => (
            <StageRow key={s} stage={s} current={status} onPick={onChange} close={close} note={CLOSED_NOTE[s]} />
          ))}
        </>
      )}
    </Menu>
  );
}

function StageRow({
  stage, current, onPick, close, note,
}: {
  stage: Status; current: string; onPick: (s: Status) => void; close: () => void; note?: string;
}) {
  const active = stage === current;
  return (
    <MenuItem
      icon={<span className={`inline-block w-2 h-2 rounded-full ${active ? STAGE_DOT[stage] ?? 'bg-muted-2' : 'bg-line'}`} />}
      note={note}
      trailing={active ? '✓' : undefined}
      tone={stage === 'Not Interested' && !active ? 'danger' : 'default'}
      onClick={() => { if (!active) onPick(stage); close(); }}
    >
      {stageLabel(stage)}
    </MenuItem>
  );
}

export { isClosedStage };
